import React, { useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider } from "./hooks/useAuth";
import ProtectedRoute from "./components/ProtectedRoute";
import { PendingActionProvider } from "./hooks/usePendingAction";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";

// ─── Lazy: User layouts & pages ─────────────────────────────────────────────
const UserLayout        = lazy(() => import("./layouts/UserLayout"));
const Home              = lazy(() => import("./pages/Home"));
const Products          = lazy(() => import("./pages/Products"));
const ProductDetails    = lazy(() => import("./pages/ProductDetails"));
const Cart              = lazy(() => import("./pages/Cart"));
const Wishlist          = lazy(() => import("./pages/Wishlist"));
const ProductEnquiry    = lazy(() => import("./pages/ProductEnquiry"));
const Checkout          = lazy(() => import("./pages/Checkout"));
const OrderConfirm      = lazy(() => import("./pages/OrderPages/OrderConfirm"));
const OrderDetails      = lazy(() => import("./pages/OrderPages/OrderDetails"));
const Design            = lazy(() => import("./pages/Design"));
const Contact           = lazy(() => import("./pages/Contact"));
const OrderHistory      = lazy(() => import("./pages/OrderHistory"));
const TrackOrder        = lazy(() => import("./pages/TrackOrder"));
const SingleOrderDetails= lazy(() => import("./pages/SingleOrderDetails"));
const ProfilePage       = lazy(() => import("./pages/ProfilePage"));
const AboutUs           = lazy(() => import("./pages/Aboutus"));
const TermsAndConditions= lazy(() => import("./pages/TermsAndConditions"));
const VisualizerPage    = lazy(() => import("./Pages1/VisualizerPage"));

// Auth modals
const AuthModal         = lazy(() => import("./components/AuthModal"));
const LoginForm         = lazy(() => import("./pages/LoginForm"));
const RegisterForm      = lazy(() => import("./pages/RegisterForm"));
const ForgotPassword    = lazy(() => import("./pages/ForgotPassword"));

// ─── Lazy: Admin layout & pages ─────────────────────────────────────────────
const AdminLayout         = lazy(() => import("./admin/layout/AdminLayout"));
const Dashboard           = lazy(() => import("./admin/pages/Dashboard"));
const ProductCategory     = lazy(() => import("./admin/pages/ProductCategory"));
const ProductBrand        = lazy(() => import("./admin/pages/ProductBrand"));
const ProductSubCategory  = lazy(() => import("./admin/pages/ProductSubCategory"));
const AddProduct          = lazy(() => import("./admin/pages/AddProduct"));
const ProductEdit         = lazy(() => import("./admin/pages/ProductEdit"));
const ProductVariant      = lazy(() => import("./admin/pages/ProductVariant"));
const AddProductVariant   = lazy(() => import("./admin/pages/AddProductVariant"));
const ViewProduct         = lazy(() => import("./admin/pages/ViewProduct"));
const EditProductVariant  = lazy(() => import("./admin/pages/EditProductVariant"));
const Product             = lazy(() => import("./admin/pages/Product"));
const OrderManage         = lazy(() => import("./admin/pages/OrderManage"));
const ViewOrderDetails    = lazy(() => import("./admin/pages/ViewOrderDetails"));
const ProductReviewManage = lazy(() => import("./admin/pages/ProductReviewManage"));
const AddUser             = lazy(() => import("./admin/pages/AddUser"));
const EditUser            = lazy(() => import("./admin/pages/EditUser"));
const UserRoles           = lazy(() => import("./admin/pages/UserRoles"));
const Contacts            = lazy(() => import("./admin/pages/Contacts"));
const UserLists           = lazy(() => import("./admin/pages/UserLists"));
const Enquirys            = lazy(() => import("./admin/pages/Enquirys"));

// ─── Full-screen suspense fallback ───────────────────────────────────────────
// The white overlay in index.html covers the flash while this spinner shows.
function PageLoader() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#ffffff",
        zIndex: 99998,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          border: "4px solid #e5e7eb",
          borderTopColor: "#1a1a2e",
          animation: "spin 0.75s linear infinite",
        }}
      />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export default function App() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login"); // "login" | "register"
  const [showForgot, setShowForgot] = useState(false);
  const [authMessage, setAuthMessage] = useState("");
  const [isAuthenticated] = useState(true);

  const openLogin = (message = "") => {
    setAuthTab("login");
    setShowForgot(false);
    setAuthMessage(message);
    setAuthOpen(true);
  };

  const openRegister = (message = "") => {
    setAuthTab("register");
    setShowForgot(false);
    setAuthMessage(message);
    setAuthOpen(true);
  };

  const openForgot = () => {
    setShowForgot(true);
    setAuthOpen(true);
  };

  const closeAuth = () => {
    setAuthOpen(false);
    setShowForgot(false);
    setAuthMessage("");
  };

  return (
    <AuthProvider>
      <PendingActionProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AdminAuthProvider>
            <Suspense fallback={<PageLoader />}>
              {/* Global Auth Modal */}
              {authOpen && (
                <AuthModal
                  isOpen={authOpen}
                  onClose={closeAuth}
                  defaultTab={authTab}
                  hideHeader={showForgot}
                  onTabChange={(tab) => setAuthTab(tab)}
                >
                  {showForgot ? (
                    <ForgotPassword onBackToLogin={() => setShowForgot(false)} />
                  ) : authTab === "login" ? (
                    <LoginForm
                      onForgotPassword={openForgot}
                      onRegisterClick={() => openRegister()}
                      onClose={closeAuth}
                    />
                  ) : (
                    <RegisterForm onLoginClick={() => openLogin()} onClose={closeAuth} />
                  )}
                </AuthModal>
              )}

              <Routes>
                {/* ── User routes ── */}
                <Route element={<UserLayout />}>
                  <Route
                    path="/visualizer"
                    element={<VisualizerPage onLogin={openLogin} onRegister={openRegister} />}
                  />
                  <Route
                    path="/"
                    element={<Home onLogin={openLogin} onRegister={openRegister} />}
                  />
                  <Route
                    path="/design"
                    element={<Design onLogin={openLogin} onRegister={openRegister} />}
                  />
                  <Route
                    path="/products"
                    element={<Products onLogin={openLogin} onRegister={openRegister} />}
                  />
                  <Route
                    path="/product/:slug"
                    element={<ProductDetails onLogin={openLogin} onRegister={openRegister} />}
                  />
                  <Route
                    path="/cart"
                    element={
                      <ProtectedRoute>
                        <Cart onLogin={openLogin} onRegister={openRegister} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wishlist"
                    element={
                      <ProtectedRoute>
                        <Wishlist onLogin={openLogin} onRegister={openRegister} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/productEnquiry"
                    element={<ProductEnquiry onLogin={openLogin} onRegister={openRegister} />}
                  />
                  <Route
                    path="/checkout"
                    element={
                      <ProtectedRoute>
                        <Checkout onLogin={openLogin} onRegister={openRegister} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orderconfirmation"
                    element={
                      <ProtectedRoute>
                        <OrderConfirm onLogin={openLogin} onRegister={openRegister} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders"
                    element={
                      <ProtectedRoute>
                        <OrderDetails onLogin={openLogin} onRegister={openRegister} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contact"
                    element={<Contact onLogin={openLogin} onRegister={openRegister} />}
                  />
                  <Route
                    path="/orderhistory"
                    element={
                      <ProtectedRoute>
                        <OrderHistory onLogin={openLogin} onRegister={openRegister} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/track-order/:orderId"
                    element={
                      <ProtectedRoute>
                        <TrackOrder onLogin={openLogin} onRegister={openRegister} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/order-details/:orderId"
                    element={
                      <ProtectedRoute>
                        <SingleOrderDetails onLogin={openLogin} onRegister={openRegister} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/about-us"
                    element={<AboutUs onLogin={openLogin} onRegister={openRegister} />}
                  />
                  <Route
                    path="/terms-and-conditions"
                    element={<TermsAndConditions onLogin={openLogin} onRegister={openRegister} />}
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute isAuth={isAuthenticated}>
                        <ProfilePage onLogin={openLogin} onRegister={openRegister} />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* ── Admin routes ── */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="productCategory" element={<ProductCategory />} />
                  <Route path="productbrand" element={<ProductBrand />} />
                  <Route path="productsubcategory" element={<ProductSubCategory />} />
                  <Route path="contacts" element={<Contacts />} />
                  <Route path="userlists" element={<UserLists />} />
                  <Route path="enquirys" element={<Enquirys />} />
                  <Route path="addproduct" element={<AddProduct />} />
                  <Route path="editproduct/:id" element={<ProductEdit />} />
                  <Route path="products" element={<Product />} />
                  <Route path="viewproductbyid" element={<ViewProduct />} />
                  <Route path="productvariants" element={<ProductVariant />} />
                  <Route path="addproductvariant" element={<AddProductVariant />} />
                  <Route path="ordermanage" element={<OrderManage />} />
                  <Route path="vieworderdetails/:id" element={<ViewOrderDetails />} />
                  <Route path="editproductvariant/:id" element={<EditProductVariant />} />
                  <Route path="manageproductreview" element={<ProductReviewManage />} />
                  <Route path="adduser" element={<AddUser />} />
                  <Route path="edituser/:userId" element={<EditUser />} />
                  <Route path="userroles" element={<UserRoles />} />
                </Route>

                <Route path="/orderConfirm" element={<OrderConfirm />} />
                <Route path="/order-details" element={<OrderDetails />} />
              </Routes>
            </Suspense>
          </AdminAuthProvider>
        </BrowserRouter>
      </PendingActionProvider>
    </AuthProvider>
  );
}
